"use client";

import { SectionHeading } from "@/composites/landing";
import type { Cta } from "@/composites/landing/types";
import { normalizeImageAsset } from "@/app/utilities/images";
import { useForwardedCta } from "@/app/utilities/query-params/use-forwarded-cta";
import type { ImageAsset } from "@/types/references";
import {
	KitOptionsGrid,
	type KitCardData
} from "../../components/consultant-agreement";
import * as styles from "./KitOptions.module.scss";

export interface LandingContentReference {
	referenceId: string;
	contentType: string;
}
export interface KitOptionsKitEntry {
	badge_text?: string;
	sale_badge_text?: string;
	kit_product?: LandingContentReference[];
	image?: ImageAsset;
	price_text?: string;
	original_price_text?: string;
	percent_discount_text?: string;
	marketing_description?: string;
	select_cta?: Cta;
	view_details_label?: string;
	products_in_kit?: { label: string }[];
	also_includes?: { label: string }[];
}

export interface KitOptionsBlock {
	blockType: "kit_options";
	section_title?: string;
	kits: KitOptionsKitEntry[];
	kit_disclaimer?: string;
}

export function KitOptions({ block }: { block: KitOptionsBlock }) {
	const withForwardedCta = useForwardedCta();

	const cards: KitCardData[] = block.kits.map((kit) => {
		const image = normalizeImageAsset(kit.image);
		const title = kit.image?.image_alt_text?.trim() || "";
		return {
			badge_text: kit.badge_text || undefined,
			sale_badge_text: kit.sale_badge_text || undefined,
			price_text: kit.price_text,
			original_price_text: kit.original_price_text || undefined,
			percent_discount_text: kit.percent_discount_text || undefined,
			marketing_description: kit.marketing_description,
			image,
			select_cta: withForwardedCta(kit.select_cta),
			view_details_label: kit.view_details_label,
			kit_product: {
				title,
				price_text: kit.price_text,
				original_price_text: kit.original_price_text || undefined,
				sale_badge_text: kit.sale_badge_text || undefined,
				percent_discount_text: kit.percent_discount_text || undefined,
				short_description: kit.marketing_description,
				image,
				products_in_kit: kit.products_in_kit?.map((p) => p.label) ?? [],
				also_includes: kit.also_includes?.map((a) => a.label) ?? []
			}
		};
	});

	return (
		<section data-block="kit_options">
			{block.section_title ? (
				<SectionHeading headline={block.section_title} />
			) : null}
			<KitOptionsGrid kits={cards} />
			{block.kit_disclaimer?.trim() ? (
				<p className={styles["kit-options__disclaimer"]}>
					{block.kit_disclaimer}
				</p>
			) : null}
		</section>
	);
}
